/**
 * C3 — adoption & seat-ROI compute. Pure functions over the Copilot usage rows,
 * kept out of the component so they are unit-testable and memoisable by the query
 * cache (§7). No network here.
 */
import type { CopilotUsageRow } from '@/graph/modules/copilotUsage';

export interface AppAdoption {
  key: string;
  label: string;
  activeUsers: number;
}

export interface AdoptionSummary {
  /** Users provisioned with Copilot who appear in the report (≈ assigned seats). */
  enabledSeats: number;
  /** Users with any Copilot activity in the period. */
  activeSeats: number;
  /** activeSeats / enabledSeats, 0–1. */
  adoptionRate: number;
  /** enabledSeats − activeSeats. */
  idleSeats: number;
  perApp: AppAdoption[];
  reportRefreshDate: string | null;
}

const APP_FIELDS: { key: keyof CopilotUsageRow; label: string }[] = [
  { key: 'copilotChatLastActivityDate', label: 'Chat' },
  { key: 'microsoftTeamsCopilotLastActivityDate', label: 'Teams' },
  { key: 'outlookCopilotLastActivityDate', label: 'Outlook' },
  { key: 'wordCopilotLastActivityDate', label: 'Word' },
  { key: 'excelCopilotLastActivityDate', label: 'Excel' },
  { key: 'powerPointCopilotLastActivityDate', label: 'PowerPoint' },
  { key: 'oneNoteCopilotLastActivityDate', label: 'OneNote' },
  { key: 'loopCopilotLastActivityDate', label: 'Loop' },
];

function hasDate(v: unknown): boolean {
  return typeof v === 'string' && v.trim().length > 0;
}

export function summariseAdoption(rows: CopilotUsageRow[]): AdoptionSummary {
  const enabledSeats = rows.length;
  const activeSeats = rows.filter((r) => hasDate(r.lastActivityDate)).length;
  const adoptionRate = enabledSeats > 0 ? activeSeats / enabledSeats : 0;

  const perApp: AppAdoption[] = APP_FIELDS.map(({ key, label }) => ({
    key: String(key),
    label,
    activeUsers: rows.filter((r) => hasDate(r[key])).length,
  })).sort((a, b) => b.activeUsers - a.activeUsers);

  const reportRefreshDate =
    rows.find((r) => hasDate(r.reportRefreshDate))?.reportRefreshDate ?? null;

  return {
    enabledSeats,
    activeSeats,
    adoptionRate,
    idleSeats: Math.max(0, enabledSeats - activeSeats),
    perApp,
    reportRefreshDate,
  };
}

/**
 * Seat ROI. Everything is client-side; `costPerSeatMonthly` is an admin-entered
 * figure that never leaves the browser. Wasted spend = idle seats × cost.
 */
export interface SeatRoi {
  utilization: number; // same as adoptionRate, named for the ROI frame
  monthlySpend: number;
  wastedMonthlySpend: number;
  annualWaste: number;
}

export function computeRoi(summary: AdoptionSummary, costPerSeatMonthly: number): SeatRoi {
  const cost = Number.isFinite(costPerSeatMonthly) && costPerSeatMonthly > 0 ? costPerSeatMonthly : 0;
  return {
    utilization: summary.adoptionRate,
    monthlySpend: summary.enabledSeats * cost,
    wastedMonthlySpend: summary.idleSeats * cost,
    annualWaste: summary.idleSeats * cost * 12,
  };
}
